import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: 'ADMIN' | 'EMPLOYEE'
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, emailValidationRequired } = useAuth()
  const location = useLocation()

  // Verificar si estamos en modo impersonate (admin viendo como empleado)
  const isImpersonating = !!localStorage.getItem('impersonating')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Si estamos en modo impersonate, saltarse todas las validaciones
  if (isImpersonating) {
    if (requiredRole && user?.role !== requiredRole && user?.role !== 'ADMIN') {
      return <Navigate to="/dashboard" replace />
    }
    return <>{children}</>
  }

  // Si debe cambiar contraseña, redirigir (excepto si ya está en /change-password)
  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  // Si es empleado sin email, redirigir a cambio de contraseña para que lo introduzca
  // (ocurre cuando un admin borra el email del usuario)
  if (
    emailValidationRequired &&
    user?.role === 'EMPLOYEE' &&
    !user?.email &&
    !user?.mustChangePassword &&
    location.pathname !== '/change-password'
  ) {
    return <Navigate to="/change-password" replace />
  }

  // Si es empleado y no ha verificado el email, redirigir a verificación
  // Los admins no necesitan verificar email
  // SOLO aplicar esta validación si emailValidationRequired está activo en la configuración
  if (
    emailValidationRequired &&
    user?.role === 'EMPLOYEE' &&
    !user?.emailVerified &&
    !user?.mustChangePassword &&
    location.pathname !== '/verify-email' &&
    location.pathname !== '/change-password'
  ) {
    return <Navigate to="/verify-email" replace />
  }

  if (requiredRole && user?.role !== requiredRole && user?.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}
